import { useState } from 'react';
import {
    Drawer, Box, Typography, IconButton, Stack, Button, Tooltip,
    CircularProgress, Alert,
} from '@mui/material';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
import downloadFill from '@iconify/icons-eva/download-fill';
import eyeFill from '@iconify/icons-eva/eye-fill';
import DocViewer from '../MediaViewer/DocViewer';
import VideoPlayer from '../MediaViewer/VideoPlayer';
import AudioPlayer from '../MediaViewer/AudioPlayer';
import TextViewer from '../MediaViewer/TextViewer';
import * as constants from '../../utils/constants';

/**
 * SyndicationDocumentViewer
 *
 * Reusable component that provides View + Download buttons for any document
 * in the syndication module. Uses the same viewer sub-components as the
 * company details page MediaViewer (PDF, DOC, XLS, PPT, images, video, audio).
 *
 * Uses direct file URLs in HTML elements (iframe, img, video, audio) to avoid
 * cross-origin issues with the remote PDF.js viewer.
 */

// Get file type from extension (handles URLs with query params)
function getFileType(fileName) {
    if (!fileName) return null;
    // Remove query params and hash before extracting extension
    const cleanUrl = fileName.split('?')[0].split('#')[0];
    const ext = cleanUrl.split('.').pop().toLowerCase();
    switch (ext) {
        case 'pdf': return 'PDF';
        case 'doc': case 'docx': case 'pptx': case 'ppt': case 'xls': case 'xlsx': return 'DOC';
        case 'txt': return 'TEXT';
        case 'mp3': case 'wav': case 'ogg': case 'm4a': return 'AUDIO';
        case 'mp4': case 'mov': return 'VIDEO';
        case 'jpg': case 'jpeg': case 'png': case 'gif': case 'webp': return 'IMAGE';
        default: return null;
    }
}

// Normalize a file URL to a /media/ path
function normalizeFileUrl(fileUrl) {
    if (!fileUrl) return fileUrl;
    let normalized = fileUrl;
    if (normalized.startsWith('http')) {
        try {
            normalized = new URL(normalized).pathname;
        } catch (e) { /* keep as-is */ }
    }
    if (normalized.includes('/api/v1/media/')) {
        normalized = normalized.replace('/api/v1/media/', '/media/');
    }
    if (!normalized.startsWith('/media/') && !normalized.startsWith('http')) {
        if (normalized.startsWith('media/')) {
            normalized = '/' + normalized;
        } else if (normalized.startsWith('/')) {
            // Already an absolute path, keep as-is
        } else {
            // Relative path (deal_documents/, post_investment_documents/, documents/, etc.)
            normalized = '/media/' + normalized;
        }
    }
    return normalized;
}

// Build the full media URL from a file field value
function buildFullUrl(fileUrl) {
    if (!fileUrl) return '';
    // Already a full URL
    if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) return fileUrl;
    // Relative path — prepend API base
    const apiBase = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;
    const normalized = normalizeFileUrl(fileUrl);
    return `${apiBase}${normalized}`;
}

function DocumentContent({ fileUrl, fileType: fileTypeProp }) {
    const fullUrl = buildFullUrl(fileUrl);
    const fileType = getFileType(fileUrl) || (fileTypeProp ? fileTypeProp.toUpperCase() : null);

    if (!fullUrl || !fileType) {
        return <Alert severity="warning">Unable to preview this file type.</Alert>;
    }

    switch (fileType) {
        case 'PDF':
            // Use browser's native PDF viewer — direct URL in iframe, no JS fetch needed
            return (
                <iframe
                    title="PDF Viewer"
                    src={fullUrl}
                    style={{ width: '100%', height: '85vh', border: 'none' }}
                />
            );
        case 'DOC':
            return <DocViewer src={fullUrl} />;
        case 'TEXT':
            return <TextViewer src={fullUrl} />;
        case 'IMAGE':
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <img
                        src={fullUrl}
                        alt="Document"
                        style={{ maxWidth: '100%', maxHeight: '85vh', objectFit: 'contain' }}
                    />
                </Box>
            );
        case 'VIDEO':
            return <VideoPlayer src={fullUrl} />;
        case 'AUDIO':
            return <AudioPlayer src={fullUrl} />;
        default:
            return <Alert severity="info">Preview not available for this file type.</Alert>;
    }
}

export default function SyndicationDocumentViewer({
    fileUrl,
    fileType,
    title = 'Document',
    onDownload,
    downloading = false,
    showDownload = true,
    showView = true,
    size = 'small',
    iconOnly = false,
}) {
    const [viewerOpen, setViewerOpen] = useState(false);

    if (!fileUrl) return null;

    return (
        <>
            <Stack direction="row" spacing={0.5} alignItems="center">
                {showView && (
                    iconOnly ? (
                        <Tooltip title="View Document">
                            <IconButton size={size} color="primary" onClick={() => setViewerOpen(true)}>
                                <Icon icon={eyeFill} width={size === 'small' ? 18 : 22} />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Button
                            size={size}
                            variant="outlined"
                            startIcon={<Icon icon={eyeFill} />}
                            onClick={() => setViewerOpen(true)}
                        >
                            View
                        </Button>
                    )
                )}
                {showDownload && onDownload && (
                    iconOnly ? (
                        <Tooltip title="Download">
                            <IconButton
                                size={size}
                                onClick={onDownload}
                                disabled={downloading}
                            >
                                {downloading
                                    ? <CircularProgress size={16} />
                                    : <Icon icon={downloadFill} width={size === 'small' ? 18 : 22} />
                                }
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Button
                            size={size}
                            variant="outlined"
                            startIcon={downloading ? <CircularProgress size={16} /> : <Icon icon={downloadFill} />}
                            onClick={onDownload}
                            disabled={downloading}
                        >
                            Download
                        </Button>
                    )
                )}
            </Stack>

            {/* Document Viewer Drawer */}
            <Drawer
                anchor="right"
                open={viewerOpen}
                onClose={() => setViewerOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', md: '70vw', lg: '60vw' } } }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {/* Header */}
                    <Box sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }} noWrap>
                            {title}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {showDownload && onDownload && (
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={downloading ? <CircularProgress size={14} /> : <Icon icon={downloadFill} />}
                                    onClick={onDownload}
                                    disabled={downloading}
                                >
                                    Download
                                </Button>
                            )}
                            <IconButton onClick={() => setViewerOpen(false)} size="small">
                                <Icon icon={closeFill} />
                            </IconButton>
                        </Stack>
                    </Box>
                    {/* Content */}
                    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                        {viewerOpen && <DocumentContent fileUrl={fileUrl} fileType={fileType} />}
                    </Box>
                </Box>
            </Drawer>
        </>
    );
}
